import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Search } from "lucide-react";

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

const actionColor = (a = "") => {
  const s = a.toLowerCase();
  if (s.includes("delete") || s.includes("reject") || s.includes("withdraw")) return "bg-rose-50 text-rose-700 border-rose-200";
  if (s.includes("approv") || s.includes("received") || s.includes("enroll") || s.includes("created")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s.includes("upload") || s.includes("document")) return "bg-blue-50 text-blue-700 border-blue-200";
  if (s.includes("stage") || s.includes("update") || s.includes("filing")) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
};

const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase() || "").join("") || "•";

const valuePreview = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v.length > 90 ? v.slice(0, 90) + "…" : v;
  try { const s = JSON.stringify(v); return s.length > 90 ? s.slice(0, 90) + "…" : s; } catch { return String(v); }
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lob, setLob] = useState("ALL");
  const [actor, setActor] = useState("");
  const [entity, setEntity] = useState("ALL");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(300);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (lob !== "ALL") params.set("lob", lob);
    if (actor.trim()) params.set("actor", actor.trim());
    if (entity !== "ALL") params.set("entity", entity);
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", String(limit));
    try {
      const r = await api.get(`/audit-log?${params.toString()}`);
      setRows(r.data.entries || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [lob, actor, entity, q, limit]); // eslint-disable-line

  const grouped = useMemo(() => {
    const g = new Map();
    for (const r of rows) {
      const day = (r.created_at || "").slice(0, 10) || "Unknown";
      if (!g.has(day)) g.set(day, []);
      g.get(day).push(r);
    }
    return Array.from(g.entries());
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Audit Log</h1>
          <p className="text-sm text-slate-500 mt-1">Every stage change, upload, and status update across B2B and B2C. Admin/Manager only.</p>
        </div>
        <Button data-testid="audit-refresh" variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1"/>Refresh</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <div className="text-xs text-slate-500 mb-1">Line of Business</div>
          <Select value={lob} onValueChange={setLob}>
            <SelectTrigger data-testid="audit-lob"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="B2B">B2B</SelectItem>
              <SelectItem value="B2C">B2C</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Entity (B2C)</div>
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger data-testid="audit-entity"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="student">Student</SelectItem>
              <SelectItem value="application">Application</SelectItem>
              <SelectItem value="filing">Filing</SelectItem>
              <SelectItem value="enrollment_semester">Enrollment</SelectItem>
              <SelectItem value="commission_claim">Commission</SelectItem>
              <SelectItem value="document">Document</SelectItem>
              <SelectItem value="college">College</SelectItem>
              <SelectItem value="program">Program</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Actor</div>
          <Input data-testid="audit-actor" placeholder="e.g. Priya" value={actor} onChange={e=>setActor(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <div className="text-xs text-slate-500 mb-1">Search</div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="audit-search" placeholder="Action, id, value…" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" />
          </div>
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>}
      {!loading && rows.length === 0 && <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500">No entries match these filters.</div>}

      {!loading && grouped.map(([day, items]) => (
        <div key={day} className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-slate-500">{day}</div>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {items.map(e => {
              const appPath = e.app_code
                ? (e.lob === "B2B" ? `/applications/${e.application_id}` : `/b2c/applications/${e.application_id}`)
                : null;
              return (
                <div key={e.id} data-testid={`audit-row-${e.id}`} className="flex items-start gap-3 p-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700 shrink-0">{initials(e.actor_name)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{e.actor_name || "System"}</span>
                      <span className={`stage-badge ${actionColor(e.action)}`}>{e.action}</span>
                      <span className="text-xs text-slate-400">·</span>
                      <span className="text-xs text-slate-500 uppercase">{e.lob}</span>
                      {e.entity && e.lob === "B2C" && <span className="text-xs text-slate-500">· {e.entity}</span>}
                      {appPath && (
                        <Link to={appPath} className="text-xs mono text-orange-600 hover:underline">
                          {e.app_code}{e.subject_name ? ` · ${e.subject_name}` : ""}
                        </Link>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-slate-600">
                      {e.details && <span>{e.details}</span>}
                      {(e.old_value != null || e.new_value != null) && (
                        <span>
                          {e.old_value != null && <><span className="text-slate-400">from</span> <span className="mono">{valuePreview(e.old_value)}</span> </>}
                          {e.new_value != null && <><span className="text-slate-400">→</span> <span className="mono">{valuePreview(e.new_value)}</span></>}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 shrink-0 mt-1">{fmt(e.created_at).split(",")[1]?.trim() || fmt(e.created_at)}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {!loading && rows.length >= limit && (
        <div className="text-center">
          <Button variant="outline" size="sm" onClick={()=>setLimit(l => l + 300)}>Load more</Button>
        </div>
      )}
    </div>
  );
}
